import { css } from 'styled-components';

import { COLOR, mixins, textStyles } from '#@/shared/utils/styles';

import type { TagProps } from './types';

const DEFAULT_COLOR = COLOR.textSecondary;

const getColor = (props: TagProps) => props.color || DEFAULT_COLOR;

const getGradient = (props: TagProps, opacity = 1) => {
  if (!props.gradient) return null;
  const { direction = 'to bottom', colors } = props.gradient;
  const colorStops = colors
    .map(({ color, position }) => `${mixins.rgba(color, opacity)}${position ? ` ${position}` : ''}`)
    .join(', ');
  return `linear-gradient(${direction}, ${colorStops})`;
};

const getSolidBackground = (props: TagProps, darkenAmount = 0) => {
  if (props.gradient) {
    return getGradient(props);
  }
  const color = getColor(props);
  return darkenAmount > 0 ? mixins.darken(color, darkenAmount) : color;
};

const getSemiTransparentBackground = (props: TagProps, opacity: number) =>
  props.gradient ? getGradient(props, opacity) : mixins.rgba(getColor(props), opacity);

const getBackgroundColor = (props: TagProps, state: 'default' | 'hover' | 'active') => {
  const opacityMap = { default: 0.05, hover: 0.1, active: 0.15 };
  const darkenMap = { default: 0, hover: 0.1, active: 0.2 };

  return props.solidBackground
    ? getSolidBackground(props, darkenMap[state])
    : getSemiTransparentBackground(props, opacityMap[state]);
};

export const tagStyles = css<TagProps>`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => (props.large ? '6px' : '3px')};
  max-width: min(100%, 250px);
  padding: ${(props) => (props.large ? '6px 9px' : '4px 8px')};
  background: ${(props) => getBackgroundColor(props, 'default')};
  border-radius: 8px;
  width: max-content;

  ${(props) => (props.large ? textStyles.bodyMedium450 : textStyles.bodySmall450)};
  color: ${(props) => (props.solidBackground ? COLOR.white : getColor(props))};
  user-select: none;

  hyphens: ${(props) => props.hyphens ?? 'manual'};

  span {
    ${(props) =>
      props.truncate &&
      css`
        ${mixins.truncateText()};
      `}
  }
`;

export const tagInteractiveStyles = css<TagProps>`
  ${tagStyles};

  &:hover {
    background: ${(props) => getBackgroundColor(props, 'hover')};
  }

  &:active {
    background: ${(props) => getBackgroundColor(props, 'active')};
  }
`;
